import { useMemo } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { Globe } from "lucide-react";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// [lng, lat] centroids for the supplier jurisdictions in the demo base.
const CENTROIDS = {
  GB: [-1.5, 52.5], DE: [10.4, 51.1], TW: [121, 23.7], CN: [104, 35], US: [-98, 39],
  JP: [138, 36], KR: [127.8, 36.5], IN: [79, 22], VN: [106, 16], RU: [90, 61],
  MX: [-102, 23], PL: [19, 52], CZ: [15.5, 49.8], SG: [103.8, 1.35], MY: [102, 4],
  TH: [101, 15], BR: [-51, -10], FR: [2.2, 46.2], NL: [5.3, 52.1], IT: [12.5, 42.8],
  ES: [-3.7, 40.4], CA: [-106, 56], AU: [134, -25], CH: [8.2, 46.8], SE: [15, 62],
};

const COUNTRY_NAME = {
  GB: "United Kingdom", DE: "Germany", TW: "Taiwan", CN: "China", US: "United States",
  JP: "Japan", KR: "South Korea", IN: "India", VN: "Vietnam", RU: "Russia",
  MX: "Mexico", PL: "Poland", CZ: "Czechia", SG: "Singapore", MY: "Malaysia",
  TH: "Thailand", BR: "Brazil",
};

export const SupplierMap = ({ suppliers = [] }) => {
  const markers = useMemo(() => {
    const counts = {};
    suppliers.forEach((s) => {
      const c = s.country;
      if (!c || !CENTROIDS[c]) return;
      counts[c] = (counts[c] || 0) + 1;
    });
    const max = Math.max(1, ...Object.values(counts));
    return Object.entries(counts).map(([code, count]) => ({
      code,
      count,
      coords: CENTROIDS[code],
      r: 5 + (count / max) * 11,
    }));
  }, [suppliers]);

  return (
    <div className="border border-black/5 bg-white p-5" data-testid="supplier-map">
      <h2 className="ek-eyebrow flex items-center gap-1">
        <Globe size={12} /> Supplier geography
      </h2>
      <p className="mt-1 text-sm text-ek-ink/65">
        {markers.length} jurisdictions across the supply base — bubble size reflects supplier count.
      </p>
      <div className="mt-3">
        <ComposableMap
          projectionConfig={{ scale: 145 }}
          width={900}
          height={420}
          style={{ width: "100%", height: "auto" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#ECECEF"
                  stroke="#FFFFFF"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: "none" },
                    hover: { fill: "#E0DAF7", outline: "none" },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>
          {markers.map((m) => (
            <Marker key={m.code} coordinates={m.coords} data-testid={`map-marker-${m.code}`}>
              <circle r={m.r} fill="#7737F6" fillOpacity={0.45} stroke="#7737F6" strokeWidth={1.2} />
              <title>{`${COUNTRY_NAME[m.code] || m.code}: ${m.count} supplier(s)`}</title>
              <text
                textAnchor="middle"
                y={3}
                style={{ fontFamily: "Work Sans, sans-serif", fontSize: 9, fontWeight: 700, fill: "#fff", pointerEvents: "none" }}
              >
                {m.count}
              </text>
            </Marker>
          ))}
        </ComposableMap>
      </div>
    </div>
  );
};
